import { useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import type { MatrixClient } from "matrix-js-sdk";
import { useTypingSender } from "./useTypingSender";
import { EmojiPicker } from "./EmojiPicker";
import { MicIcon, SendIcon, TrashIcon } from "./icons";
import { useVoiceRecorder } from "../voice/useVoiceRecorder";
import { sendVoiceMessage } from "../../matrix/voice";
import { formatDuration } from "../../lib/time";

interface ComposerProps {
  client: MatrixClient;
  roomId: string;
  onError: (message: string) => void;
}

const MAX_HEIGHT = 120;
const LIVE_BARS = 28;

/**
 * WhatsApp-style composer. In text mode the single right-hand button is a mic
 * when empty and a send button when there's text. Pressing the mic swaps the
 * whole row into a recording strip (delete · timer · live waveform · send) —
 * so there is only ever ONE send action.
 */
export function Composer({ client, roomId, onError }: ComposerProps) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [focused, setFocused] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const notifyTyping = useTypingSender(client, roomId);
  const rec = useVoiceRecorder();

  const autoGrow = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
  };

  const sendText = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    setText("");
    requestAnimationFrame(() => autoGrow(taRef.current));
    try {
      await client.sendTextMessage(roomId, body);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to send message.");
      setText(body);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void sendText();
    }
  };

  const insertEmoji = (emoji: string) => {
    const el = taRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    setText(text.slice(0, start) + emoji + text.slice(end));
    notifyTyping();
    requestAnimationFrame(() => {
      const node = taRef.current;
      if (!node) return;
      node.focus();
      const pos = start + emoji.length;
      node.setSelectionRange(pos, pos);
      autoGrow(node);
    });
  };

  const startRecording = async () => {
    setEmojiOpen(false);
    try {
      await rec.start();
    } catch (err) {
      const msg =
        err instanceof DOMException && err.name === "NotAllowedError"
          ? "Microphone permission was denied. Enable it to send voice messages."
          : err instanceof Error
            ? err.message
            : "Could not start recording.";
      onError(msg);
    }
  };

  const sendRecording = async () => {
    try {
      const result = await rec.stop();
      if (!result) return; // too short / nothing captured
      await sendVoiceMessage(client, roomId, result);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to send voice message.");
    }
  };

  const hasText = text.trim().length > 0;

  return (
    <div
      style={{
        position: "relative",
        padding: "var(--space-sm) var(--space-md)",
        background: "var(--color-bg-subtle)",
        borderTop: "1px solid var(--color-border)",
      }}
    >
      {emojiOpen && !rec.recording && (
        <EmojiPicker
          onPick={insertEmoji}
          onClose={() => setEmojiOpen(false)}
          style={{ position: "absolute", bottom: 66, left: 14, zIndex: 6 }}
        />
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-sm)",
          minHeight: 48,
          background: "var(--color-panel)",
          borderRadius: "var(--radius-lg)",
          border: `1px solid ${focused || rec.recording ? "var(--color-accent)" : "var(--color-border)"}`,
          boxShadow: focused || rec.recording ? "0 0 0 3px rgba(11, 35, 63, 0.10)" : "none",
          padding: "4px 6px",
          transition: "border-color .15s ease, box-shadow .15s ease",
        }}
      >
        {rec.recording ? (
          <>
            <button
              type="button"
              onClick={rec.cancel}
              aria-label="Delete recording"
              title="Delete recording"
              style={{ ...iconButton, color: "var(--color-danger)" }}
            >
              <TrashIcon />
            </button>
            <span style={recDot} />
            <span style={timerText}>{formatDuration(rec.elapsedMs)}</span>
            <div aria-label="Recording level" style={waveWrap}>
              {buildBars(rec.levels).map((bar, i) => (
                <span
                  key={i}
                  style={{
                    flex: 1,
                    minWidth: 2,
                    maxWidth: 4,
                    borderRadius: 2,
                    height: `${Math.max(12, bar.level * 100)}%`,
                    background: bar.active ? "var(--color-accent)" : "rgba(11, 35, 63, 0.3)",
                    transition: "height 0.12s ease",
                  }}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => void sendRecording()}
              aria-label="Send voice message"
              title="Send"
              style={sendButton}
            >
              <SendIcon />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              aria-label="Emoji"
              title="Emoji"
              onClick={() => setEmojiOpen((o) => !o)}
              style={iconButton}
            >
              🙂
            </button>
            <textarea
              ref={taRef}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                autoGrow(e.target);
                if (e.target.value) notifyTyping();
              }}
              onKeyDown={onKeyDown}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder="Type a message"
              rows={1}
              aria-label="Message"
              style={{
                flex: 1,
                resize: "none",
                maxHeight: MAX_HEIGHT,
                border: "none",
                background: "transparent",
                padding: "6px 4px",
                fontFamily: "inherit",
                fontSize: "var(--font-size-md)",
                lineHeight: 1.4,
                color: "var(--color-text)",
                outline: "none",
              }}
            />
            {hasText ? (
              <button
                type="button"
                onClick={() => void sendText()}
                disabled={sending}
                aria-label="Send"
                title="Send"
                style={{ ...sendButton, opacity: sending ? 0.5 : 1 }}
              >
                <SendIcon />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void startRecording()}
                disabled={!rec.supported}
                aria-label="Record voice message"
                title={rec.supported ? "Record voice message" : "Voice recording not supported"}
                style={{ ...iconButton, opacity: rec.supported ? 1 : 0.4 }}
              >
                <MicIcon />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

interface Bar {
  level: number;
  active: boolean;
}

/** Right-aligned rolling buffer: newest sample at the far right, brighter. */
function buildBars(levels: number[]): Bar[] {
  return Array.from({ length: LIVE_BARS }, (_, i) => {
    const offset = i - (LIVE_BARS - levels.length);
    const level = offset >= 0 ? (levels[offset] ?? 0) : 0;
    return { level, active: LIVE_BARS - 1 - i < 8 };
  });
}

const iconButton: CSSProperties = {
  width: 40,
  height: 40,
  flexShrink: 0,
  border: "none",
  background: "transparent",
  color: "var(--color-text-muted)",
  cursor: "pointer",
  fontSize: 18,
  borderRadius: "var(--radius-pill)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const sendButton: CSSProperties = {
  width: 40,
  height: 40,
  flexShrink: 0,
  border: "none",
  borderRadius: "var(--radius-pill)",
  background: "var(--color-accent)",
  color: "var(--color-accent-contrast)",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "transform .1s ease, opacity .15s ease",
};

const recDot: CSSProperties = {
  width: 10,
  height: 10,
  flexShrink: 0,
  marginLeft: 2,
  borderRadius: "50%",
  background: "var(--color-danger)",
  animation: "cmc-pulse 1.2s ease-in-out infinite",
};

const timerText: CSSProperties = {
  flexShrink: 0,
  minWidth: 42,
  textAlign: "center",
  color: "var(--color-danger)",
  fontSize: "var(--font-size-sm)",
  fontVariantNumeric: "tabular-nums",
};

const waveWrap: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  height: 26,
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
};
